/**
 * What may be written in a table column, and whether it was.
 *
 * A COLUMN TYPE IS A CONSTRAINT, NOT A FORMAT -- so unlike a border weight or a column width it
 * cannot live only here. The server decides; this file exists so the editor can say "that is not a
 * date" while somebody types rather than at submission, and a round trip per keystroke is not a
 * thing to build.
 *
 * THE RULE IS THEREFORE WRITTEN TWICE, here and in cres-aws-t11 cres_t11/service/column_types.py.
 * That is a real cost and the mitigation is specific: the same table of cases appears in both test
 * suites (columnTypes.test.ts and test_column_types.py), so the two drift only if somebody edits
 * one and not the other with both suites green, which the cases make hard.
 *
 * DECLARED ON THE TABLE, INDEXED BY COLUMN -- not on each cell the way `colwidth` is. A new row's
 * cells are built with default attributes, so a constraint kept per cell would silently vanish for
 * every row added after it was set, and a constraint that disappears when you press Tab is worse
 * than no constraint at all. The array lives on the table, which rows cannot affect, and column
 * insert and delete shift it.
 */

export type ColumnKind = "number" | "date" | "choice";

export type ColumnType = { kind: ColumnKind; values?: string[] } | null;

export const KINDS: ColumnKind[] = ["number", "date", "choice"];

export const KIND_LABEL: Record<ColumnKind, string> = {
    number: "Number",
    date: "Date",
    choice: "One of a list",
};

// -1,234.56 / 42 / +0.5. Thousands separators because people paste out of spreadsheets.
const NUMBER = /^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAMED_DATE = /^(\d{1,2})\s+([A-Za-z]{3,9})\.?\s+(\d{4})$/;
const SLASHED = /^\d{1,2}[/.]\d{1,2}[/.]\d{2,4}$/;

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isRealDate(year: number, month: number, day: number): boolean {
    if (month < 1 || month > 12 || day < 1) return false;
    if (month === 2 && day === 29) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return day <= DAYS_IN_MONTH[month - 1];
}

function looksLikeADate(text: string): boolean {
    const iso = ISO_DATE.exec(text);
    if (iso) return isRealDate(+iso[1], +iso[2], +iso[3]);

    const named = NAMED_DATE.exec(text);
    if (named) {
        const month = MONTHS.indexOf(named[2].slice(0, 3).toLowerCase()) + 1;
        return month > 0 && isRealDate(+named[3], month, +named[1]);
    }
    return false;
}

/**
 * Why this text fails the type, or null if it does not.
 *
 * EMPTY IS ALWAYS ALLOWED. A type says what a value must look like, not that there must be one; a
 * blank cell in a procedure is an ordinary thing. "Required" is a different feature.
 *
 * SLASHED DATES ARE REFUSED ON PURPOSE. 01/02/2026 is the 1st of February to most of the world and
 * the 2nd of January in the United States, and a procedure read in both places must not contain a
 * date whose meaning depends on the reader.
 */
export function violation(type: ColumnType, raw: string): string | null {
    if (!type) return null;
    const text = (raw || "").trim();
    if (!text) return null;

    if (type.kind === "number") return NUMBER.test(text) ? null : "is not a number";

    if (type.kind === "date") {
        if (looksLikeADate(text)) return null;
        if (SLASHED.test(text)) {
            return "is a date nobody can read without knowing where it was written — "
                 + "use 2026-10-06 or 6 Oct 2026";
        }
        return "is not a date — use 2026-10-06 or 6 Oct 2026";
    }

    if (type.kind === "choice") {
        const allowed = (type.values || []).map((v) => String(v).trim()).filter(Boolean);
        if (!allowed.length) return null;      // a list with nothing in it constrains nothing
        if (allowed.some((a) => a.toLowerCase() === text.toLowerCase())) return null;
        return `is not one of ${allowed.join(", ")}`;
    }

    return null;
}

/** The types declared on a table node, as an array indexed by column. */
export function declaredTypes(table: any): ColumnType[] {
    const declared = table?.attrs?.columnTypes;
    if (!Array.isArray(declared)) return [];
    return declared.map((entry: any) =>
        (entry && typeof entry === "object" && KINDS.includes(entry.kind))
            ? { kind: entry.kind as ColumnKind, values: entry.values }
            : null);
}

/** `types` with `width` entries, so an index past the end reads as "no constraint". */
export function padded(types: ColumnType[], width: number): ColumnType[] {
    const out = types.slice(0, width);
    while (out.length < width) out.push(null);
    return out;
}

/** An array with the entry at `index` replaced -- for setting one column's type. */
export function withTypeAt(types: ColumnType[], index: number, type: ColumnType): ColumnType[] {
    const out = padded(types, Math.max(types.length, index + 1));
    out[index] = type;
    // Trailing nulls carry no information and would grow the attribute every time a column is added.
    while (out.length && !out[out.length - 1]) out.pop();
    return out;
}

/** The array after a column is inserted at `index` -- every later column shifts right. */
export function afterColumnInserted(types: ColumnType[], index: number): ColumnType[] {
    const out = types.slice();
    out.splice(index, 0, null);
    while (out.length && !out[out.length - 1]) out.pop();
    return out;
}

/** The array after the column at `index` is removed. */
export function afterColumnRemoved(types: ColumnType[], index: number): ColumnType[] {
    const out = types.slice();
    out.splice(index, 1);
    while (out.length && !out[out.length - 1]) out.pop();
    return out;
}
